import { useState } from "react";
import { Check, Copy, KeyRound, Link2 } from "lucide-react";
import type { InvitationCredentials } from "@/lib/api";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatTimestamp } from "@/lib/format";

export function InvitationCredentialsDialog({
  credentials,
  onClose,
}: {
  credentials: InvitationCredentials | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState<"link" | "password" | null>(null);
  if (!credentials) return null;

  async function copy(kind: "link" | "password", value: string) {
    await navigator.clipboard.writeText(value);
    setCopied(kind);
    window.setTimeout(() => setCopied(null), 1600);
  }

  return (
    <Dialog open onClose={onClose} title="Temporary access ready">
      <div className="space-y-4 overflow-y-auto p-4 sm:p-5">
        <p className="text-sm leading-6 text-studio-muted">Share either option securely. These credentials are shown once and expire on {formatTimestamp(credentials.expires_at)}.</p>
        <CredentialRow icon={<Link2 className="h-4 w-4" />} label="Activation link" value={credentials.invite_url} copied={copied === "link"} onCopy={() => void copy("link", credentials.invite_url)} />
        <CredentialRow icon={<KeyRound className="h-4 w-4" />} label="Temporary password" value={credentials.temporary_password} copied={copied === "password"} onCopy={() => void copy("password", credentials.temporary_password)} />
        <p role="status" className="rounded-xl bg-studio-warning/10 px-3 py-2 text-xs leading-5 text-studio-warning">Closing this window permanently hides both secrets. Regenerating credentials invalidates the previous pair.</p>
        <div className="flex justify-end"><Button onClick={onClose}>Done</Button></div>
      </div>
    </Dialog>
  );
}

function CredentialRow({ icon, label, value, copied, onCopy }: { icon: React.ReactNode; label: string; value: string; copied: boolean; onCopy: () => void }) {
  return (
    <div className="rounded-xl border border-studio-border bg-studio-card p-3">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-studio-muted">{icon}{label}</div>
      <div className="flex items-center gap-2"><code className="min-w-0 flex-1 break-all rounded-lg bg-studio-bg px-3 py-2 font-mono text-xs text-studio-ink">{value}</code><Button type="button" size="icon" variant="secondary" aria-label={`Copy ${label}`} onClick={onCopy}>{copied ? <Check className="h-4 w-4 text-studio-success" /> : <Copy className="h-4 w-4" />}</Button></div>
    </div>
  );
}
